<template>
  <div>
    <!-- KPI band -->
    <section id="sd-kpis" class="sd-kpis">
      <sd-kpi-card :label="t('k_surveys')" icon="mdi-file-document-outline" :value="fmt(S.total)" :footnote="`${fmt(S.questions)} ${t('q')}`" />
      <sd-kpi-card :label="t('k_parts')" icon="mdi-account-group-outline" :value="compact(P.total)" :spark="pSpark" :delta="pDelta" delta-unit="%" :footnote="pDelta != null ? t('vs2023') : ''" />
      <sd-kpi-card :label="t('k_appr')" icon="mdi-check-circle-outline" :value="fmt(S.approved)" :footnote="`${t('of_t')} ${fmt(S.total)} ${t('survey_u')}`" />
      <sd-kpi-card :label="t('k_time')" icon="mdi-clock-outline" :value="(P.avgMinutes || 0).toFixed(1)" :unit="' ' + t('minute')" />
    </section>

    <!-- quick read -->
    <div v-if="quick" class="callout">
      <span class="co-badge">{{ t('qr_badge') }}</span><h3>{{ t('qr_title') }}</h3>
      <p v-html="quick" />
      <a @click.prevent="scrollTo('sec-ops')">{{ t('qr_all') }}</a>
    </div>

    <!-- year filter -->
    <div v-if="years.length" class="sd-tool">
      <span class="ylab">{{ t('yr_lbl') }}</span>
      <v-btn-toggle v-model="yearIdx" mandatory dense>
        <v-btn small text>{{ t('yr_all') }}</v-btn>
        <v-btn v-for="y in years" :key="y" small text>{{ y }}</v-btn>
      </v-btn-toggle>
      <span class="yscope">{{ t('yr_scope') }}</span>
    </div>

    <!-- geography -->
    <sd-section-title id="sec-geo" :title="t('s_geo')" :note="t('s_geo_n')" />
    <section class="sd-grid">
      <div class="sd-card c7">
        <div class="head">
          <div><h3>{{ t('c_map') }}</h3><p class="hint">{{ t('c_map_h') }}</p></div>
          <v-btn-toggle v-model="metric" mandatory dense>
            <v-btn small text value="part">{{ t('m_part') }}</v-btn>
            <v-btn small text value="sat">{{ t('m_sat') }}</v-btn>
          </v-btn-toggle>
        </div>
        <sd-saudi-map :stats="mapStats" :metric="metric" />
      </div>
      <div class="sd-card c5">
        <div class="head"><div><h3>{{ t('c_rank') }}</h3><p class="hint">{{ t('c_rank_h') }}{{ yearSuffix }}</p></div></div>
        <sd-meter-rows :key="'rank' + year" :items="rankRows" medals />
      </div>
    </section>

    <!-- satisfaction & trends -->
    <sd-section-title id="sec-sat" :title="t('s_sat')" :note="t('s_sat_n')" />
    <section class="sd-grid">
      <div class="sd-card c8">
        <div class="head"><div><h3>{{ t('c_trend') }}</h3><p class="hint">{{ t('c_trend_h') }}</p></div></div>
        <sd-monthly-area :data="trend" :unit="t('part_u')" />
      </div>
      <div class="sd-card c4">
        <div class="head"><div><h3>{{ t('c_demo') }}</h3><p class="hint">{{ t('c_demo_h') }}</p></div></div>
        <div style="display:flex;gap:10px;justify-content:space-around;flex-wrap:wrap">
          <div style="text-align:center">
            <sd-donut :items="gender" :center="pctS(DM.malePct || 0)" :sub="t('male')" />
            <div class="sd-legend" style="justify-content:center"><span v-for="g in gender" :key="g.label" class="lg"><span class="sw" :style="{background: g.c}" />{{ g.label }}</span></div>
          </div>
          <div style="text-align:center">
            <sd-donut :items="nat" :center="pctS(DM.saudiPct || 0)" :sub="t('saudi')" />
            <div class="sd-legend" style="justify-content:center"><span v-for="g in nat" :key="g.label" class="lg"><span class="sw" :style="{background: g.c}" />{{ g.label }}</span></div>
          </div>
        </div>
        <div style="margin-top:14px;border-top:1px solid var(--sd-line-2);padding-top:13px">
          <h3 style="font-size:13.5px;margin-bottom:10px">{{ t('c_sector') }}</h3>
          <div v-for="s in sectors" :key="s.label" class="secbar">
            <div class="lab"><span>{{ s.label }}</span><b class="tnum num-ltr">{{ s.p }}%</b></div>
            <div class="minibar"><i :style="{width: (ready ? s.p : 0) + '%', background: s.c}" /></div>
          </div>
        </div>
      </div>
    </section>

    <!-- services & governance -->
    <sd-section-title id="sec-ops" :title="t('s_ops')" :note="t('s_ops_n')" />
    <section class="sd-grid">
      <div class="sd-card c4">
        <div class="head"><div><h3>{{ t('c_sla') }}</h3><p class="hint">{{ t('c_sla_h') }}</p></div></div>
        <div class="gauge-wrap">
          <sd-gauge :rate="comp.rate" />
          <div style="flex:1;min-width:130px">
            <div style="font-size:12.5px;color:var(--sd-ink-3)">{{ t('sla_rate') }}</div>
            <div style="font-size:13px;color:var(--sd-ink-2);margin-top:6px;line-height:1.7"><b class="tnum">{{ fmt(comp.done) }}</b> {{ t('completed') }} · <b class="tnum">{{ fmt(comp.by.InProgress || 0) }}</b> {{ t('inprogress') }} · <b class="tnum">{{ fmt(comp.by.Draft || 0) }}</b> {{ t('draft') }}</div>
          </div>
        </div>
        <div class="slagrid">
          <div v-for="b in slaBoxes" :key="b.txt" class="slabox"><span class="edge" :style="{background: b.c}" /><div class="n tnum" :style="{color: b.c}">{{ b.n }}</div><div class="t">{{ b.txt }}</div></div>
        </div>
      </div>
      <div class="sd-card c4">
        <div class="head"><div><h3>{{ t('c_svc') }}</h3><p class="hint">{{ t('c_svc_h') }}</p></div></div>
        <div v-for="a in svc" :key="a.label" class="svcrow">
          <div class="lab"><span>{{ a.label }}</span><b class="tnum" style="flex:none">{{ a.total }}</b></div>
          <div class="stk"><i v-for="s in a.segs" :key="s.s" v-tip="`<b>${esc(a.label)}</b><br>${s.label}: ${s.v}`" :style="{width: (ready ? s.p : 0) + '%', background: s.c}" /></div>
        </div>
        <div class="sd-legend"><span v-for="s in svcLegend" :key="s.label" class="lg"><span class="sw" :style="{background: s.c}" />{{ s.label }}</span></div>
      </div>
      <div class="sd-card c4">
        <div class="head"><div><h3>{{ t('c_ai') }}</h3><p class="hint">{{ t('c_ai_h') }}</p></div><span class="ai-badge">✦ {{ t('ai_b') }}</span></div>
        <sd-insight-list :items="insights" />
      </div>
      <div class="sd-card c12">
        <div class="head">
          <div><h3>{{ t('c_lb') }}</h3><p class="hint">{{ lbHint }}</p></div>
          <v-text-field v-model="lbQ" class="sd-search" dense outlined hide-details prepend-inner-icon="mdi-magnify" :placeholder="t('lb_search')" />
        </div>
        <div class="lb-cols">
          <table class="lb">
<tbody>
            <tr v-for="(r, i) in lbRows" :key="r.id" @click="$emit('navigate', { name: 'survey', id: r.id })">
              <td style="width:30px"><span class="rank">{{ i + 1 }}</span></td>
              <td><div style="font-weight:600;font-size:12.5px;margin-bottom:5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:330px">{{ surveyName(r, lang) }}</div><div class="minibar"><i :style="{width: (ready ? Math.round(r.participants / lbMax * 100) : 0) + '%'}" /></div></td>
              <td style="width:60px;font-weight:750" class="tnum">{{ compact(r.participants) }}</td>
            </tr>
          </tbody>
</table>
          <div v-if="!lbRows.length" class="sd-hint" style="padding:20px">{{ t('no_data') }}</div>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import { sdMixin } from '../store.js'
import { fmt, compact, pctS, esc, surveyName, d1 } from '../lib/format.js'
import { foldedRegionStats, satStats, matrixYears, regionRanking, regionName, participantsByYear, completion } from '../lib/stats.js'
import { mainInsights, quickRead } from '../lib/insights.js'
import SdKpiCard from '../components/KpiCard.vue'
import SdSectionTitle from '../components/SectionTitle.vue'
import SdSaudiMap from '../components/charts/SaudiMap.vue'
import SdMeterRows from '../components/MeterRows.vue'
import SdMonthlyArea from '../components/charts/MonthlyArea.vue'
import SdDonut from '../components/charts/Donut.vue'
import SdGauge from '../components/charts/Gauge.vue'
import SdInsightList from '../components/InsightList.vue'
export default {
  name: 'SdIndicatorsView',
  components: { SdKpiCard, SdSectionTitle, SdSaudiMap, SdMeterRows, SdMonthlyArea, SdDonut, SdGauge, SdInsightList },
  mixins: [sdMixin],
  props: { initialYear: { type: Number, default: null }, initialMetric: { type: String, default: 'part' }, section: String },
  data() { return { yearIdx: 0, metric: this.initialMetric, lbQ: '', ready: false } },
  computed: {
    S() { return this.D.surveys || {} }, P() { return this.D.participants || {} }, DM() { return this.D.demographics || {} },
    years() { return matrixYears(this.D) },
    year() { return this.yearIdx > 0 ? this.years[this.yearIdx - 1] : null },
    yearSuffix() { return this.year ? ` · ${this.t('yr_tag')} ${this.year}` : '' },
    pByYear() { return participantsByYear(this.D) },
    pDelta() { const b = this.pByYear; return b[2024] && b[2023] ? d1((b[2024] - b[2023]) / b[2023] * 100) : null },
    pSpark() { return (this.D.trend || []).filter((d) => d.value > 50).map((d) => d.value) },
    quick() { return quickRead(this.D, this.lang) },
    mapStats() {
      const base = foldedRegionStats(this.D), yr = satStats(this.D, this.year).regions, out = {}
      Object.keys(base).forEach((k) => { const y = yr[k] || {}; out[k] = { participants: base[k].participants, rated: y.rated || 0, satPct: y.satPct != null ? y.satPct : null } })
      return out
    },
    rankRows() {
      return regionRanking(this.D, this.year).map((r) => ({ key: r.region, name: regionName(r.region, this.lang), pct: r.satPct,
        tip: `<b>${regionName(r.region, this.lang)}</b><br><span class="tt-k">${this.t('m_sat')}:</span> ${pctS(r.satPct)}<br><span class="tt-k">${this.t('rated_u')}:</span> ${fmt(r.rated)}<br><span class="tt-k">${this.t('m_part')}:</span> ${fmt(r.participants)}` }))
    },
    trend() { return (this.D.trend || []).filter((d) => d.value > 50) },
    gender() { return [{ label: this.t('male'), v: this.DM.male, c: 'var(--sd-blue)' }, { label: this.t('female'), v: this.DM.female, c: 'var(--sd-pink)' }] },
    nat() { return [{ label: this.t('saudi'), v: this.DM.saudi, c: 'var(--sd-aqua)' }, { label: this.t('nonsaudi'), v: this.DM.nonSaudi, c: 'var(--sd-neu)' }] },
    sectors() {
      const cols = ['var(--sd-green-500)', 'var(--sd-gold-500)', 'var(--sd-violet)']
      const items = (this.D.sectors || []).map((s, i) => ({ label: this.isAr ? s.nameAr : s.nameEn, v: s.participants, c: cols[i % 3] }))
      const tot = items.reduce((a, b) => a + b.v, 0) || 1
      return items.map((it) => ({ ...it, p: d1(it.v / tot * 100) }))
    },
    comp() { return completion(this.D) },
    slaBoxes() { const sla = this.D.sla || {}; return [
      { n: sla.openRequests || 0, txt: this.t('open_req'), c: 'var(--sd-warn)' },
      { n: sla.avgOpenDays || 0, txt: `${this.t('avg_age')} (${this.t('day_u')})`, c: 'var(--sd-crit)' },
      { n: sla.maxOpenDays || 0, txt: `${this.t('max_age')} (${this.t('day_u')})`, c: 'var(--sd-crit)' }] },
    svcMeta() { return { cmap: { Completed: 'var(--sd-good)', InProgress: 'var(--sd-warn)', Draft: 'var(--sd-neu)', Canceled: 'var(--sd-crit)' }, kmap: { Completed: 'completed', InProgress: 'inprogress', Draft: 'draft', Canceled: 'canceled' } } },
    svc() {
      const items = (this.D.governance || {}).items || []; const types = {}
      items.forEach((it) => { const nm = ((this.isAr ? it.typeAr : it.typeEn) || '').trim(); const tp = types[nm] || (types[nm] = { label: nm, total: 0, seg: {} }); tp.total += it.count; tp.seg[it.status] = (tp.seg[it.status] || 0) + it.count })
      const arr = Object.values(types).sort((a, b) => b.total - a.total).slice(0, 8); const max = Math.max(1, ...arr.map((a) => a.total))
      return arr.map((a) => ({ ...a, segs: ['Completed', 'InProgress', 'Draft', 'Canceled'].filter((s) => a.seg[s]).map((s) => ({ s, v: a.seg[s], p: a.seg[s] / max * 100, c: this.svcMeta.cmap[s], label: this.t(this.svcMeta.kmap[s]) })) }))
    },
    svcLegend() { return ['Completed', 'InProgress', 'Draft'].map((s) => ({ label: this.t(this.svcMeta.kmap[s]), c: this.svcMeta.cmap[s] })) },
    insights() { return mainInsights(this.D, this.lang) },
    lbAll() { return this.D.leaderboard || [] },
    lbRows() { const q = this.lbQ.trim().toLowerCase(); return q ? this.lbAll.filter((r) => ((r.nameAr || '') + ' ' + (r.nameEn || '')).toLowerCase().includes(q)).slice(0, 30) : this.lbAll.slice(0, 10) },
    lbMax() { return (this.lbAll[0] || {}).participants || 1 },
    lbHint() { return this.lbQ.trim() ? this.t('lb_found', { m: this.lbRows.length, n: this.lbAll.length }) : this.t('lb_top', { n: this.lbAll.length }) },
  },
  watch: { section: { immediate: true, handler(s) { if (s) this.$nextTick(() => this.scrollTo(s)) } } },
  mounted() {
    if (this.initialYear && this.years.includes(this.initialYear)) this.yearIdx = this.years.indexOf(this.initialYear) + 1
    requestAnimationFrame(() => requestAnimationFrame(() => { this.ready = true }))
  },
  methods: {
    fmt, compact, pctS, esc, surveyName,
    scrollTo(id) { const n = this.$el && this.$el.querySelector('#' + id); if (n) n.scrollIntoView({ behavior: this.staticMode ? 'auto' : 'smooth', block: 'start' }) },
  },
}
</script>
